import Fuse, {IFuseOptions} from 'fuse.js';
import {useCallback, useMemo, useState} from 'react';
import articlesData from '../articles.json';
import {Article, ArticleSearchResult} from '../types';
import {useHelpContentLocale} from './useHelpLocale';

const allArticles = articlesData as Article[];

const fuseOptions: IFuseOptions<Article> = {
  keys: [
    {name: 'title', weight: 2},
    {name: 'plainText', weight: 1},
    {name: 'category', weight: 0.5},
  ],
  threshold: 0.4,
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

export function useArticleSearch() {
  const [query, setQuery] = useState('');
  // URL-driven, same as useArticles: searching on /help/fr/* searches French,
  // bare /help/* searches English.
  const locale = useHelpContentLocale();

  // Filter articles by the URL locale, fallback to English
  const articles = useMemo<Article[]>(() => {
    const localeArticles = allArticles.filter((a) => a.locale === locale);
    if (localeArticles.length > 0) {
      return localeArticles;
    }
    return allArticles.filter((a) => a.locale === 'en');
  }, [locale]);

  const fuse = useMemo(() => new Fuse(articles, fuseOptions), [articles]);

  const results = useMemo<ArticleSearchResult[]>(() => {
    if (!query.trim()) {
      return [];
    }

    const searchResults = fuse.search(query);
    return searchResults.map((result) => ({
      id: result.item.id,
      slug: result.item.slug,
      title: result.item.title,
      category: result.item.category,
      score: result.score ?? 0,
    }));
  }, [fuse, query]);

  const search = useCallback(
    (searchQuery: string): ArticleSearchResult[] => {
      if (!searchQuery.trim()) {
        return [];
      }
      const searchResults = fuse.search(searchQuery);
      return searchResults.map((result) => ({
        id: result.item.id,
        slug: result.item.slug,
        title: result.item.title,
        category: result.item.category,
        score: result.score ?? 0,
      }));
    },
    [fuse],
  );

  return {
    query,
    setQuery,
    results,
    search,
  };
}
